import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useEosTransaction } from '@/hooks/useEosTransaction'
import { useWalletStore } from '@/stores/useWalletStore'
import { eosTool } from '@/services/blockchain/eos-tool'
import { isNameEncodedKey, nameToUint64, uint64ToName } from '@/lib/eos-name'

type ConfigRow = { key: string; value: string }

const CONFIG_CONTRACT = 'swap.ttr'

function normalizeValue(raw: unknown): string {
  return typeof raw === 'number' ? String(raw) : String(raw ?? '')
}

export function ManageSetupPage() {
  const { isLogin, session } = useWalletStore()
  const { transact, loading: txLoading } = useEosTransaction()

  const [rows, setRows] = useState<ConfigRow[]>([])
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editingInput, setEditingInput] = useState('')

  const loadConfigs = useCallback(async () => {
    setFetching(true)
    setFetchError(null)
    try {
      const raw = await eosTool.getTableRows<{ key: string; value: number | string }>({
        code: CONFIG_CONTRACT,
        scope: CONFIG_CONTRACT,
        table: 'configs',
        json: true,
        limit: 1000,
      })
      const mapped: ConfigRow[] = (raw ?? []).map((r) => ({
        key: r.key,
        value: normalizeValue(r.value),
      }))
      setRows(mapped)
    } catch (err) {
      console.error(err)
      setFetchError('Failed to load configs')
      toast.error('Failed to load configs')
    } finally {
      setFetching(false)
    }
  }, [])

  useEffect(() => {
    loadConfigs()
  }, [loadConfigs])

  const startEdit = (row: ConfigRow) => {
    setEditingKey(row.key)
    setEditingInput(isNameEncodedKey(row.key) ? uint64ToName(row.value) : row.value)
  }

  const cancelEdit = () => {
    setEditingKey(null)
    setEditingInput('')
  }

  const submitEdit = async (row: ConfigRow) => {
    if (!session) {
      toast.warning('Connect your wallet first')
      return
    }

    let valueUint64: string
    try {
      if (isNameEncodedKey(row.key)) {
        valueUint64 = nameToUint64(editingInput.trim())
      } else {
        const n = editingInput.trim()
        if (!/^\d+$/.test(n)) {
          toast.error('Value must be a non-negative integer')
          return
        }
        valueUint64 = n
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid value')
      return
    }

    const account = session.account
    const actions = [
      {
        account: CONFIG_CONTRACT,
        name: 'setconfig',
        authorization: [
          { actor: account.name, permission: account.authority || 'active' },
        ],
        data: { key: row.key, value: valueUint64 },
      },
    ]

    const result = await transact(actions, 'Config updated')
    if (result) {
      cancelEdit()
      await loadConfigs()
    }
  }

  if (!isLogin) {
    return (
      <div className="w-full flex justify-center px-4 md:px-[32px] md:mt-[32px]">
        <div className="w-full max-w-[720px] bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px] text-center text-[#999] text-[14px]">
          Connect your wallet to manage configs.
        </div>
      </div>
    )
  }

  return (
    <div className="w-full flex justify-center px-4 md:px-[32px] md:mt-[32px]">
      <div className="w-full max-w-[720px]">
        <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[20px]">
          <div className="flex items-center justify-between mb-[16px]">
            <div>
              <div className="text-[18px] font-semibold">Swap Config Setup</div>
              <div className="text-[12px] text-[#999]">Contract: {CONFIG_CONTRACT}</div>
            </div>
            <Button variant="outline" size="sm" onClick={loadConfigs} disabled={fetching}>
              {fetching ? 'Reloading…' : 'Reload'}
            </Button>
          </div>

          {fetching && rows.length === 0 ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : fetchError && rows.length === 0 ? (
            <div className="text-center py-[40px] text-[14px] text-[#c33]">
              {fetchError}
              <div className="mt-[12px]">
                <Button variant="outline" size="sm" onClick={loadConfigs}>Retry</Button>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-[40px] text-[14px] text-[#999]">No configs found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[14px]">
                <thead>
                  <tr className="border-b border-[#eee] text-[#666]">
                    <th className="py-2 pr-2 w-[40px]">#</th>
                    <th className="py-2 pr-2">Key</th>
                    <th className="py-2 pr-2">Value</th>
                    <th className="py-2 w-[160px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const isEditing = editingKey === row.key
                    const isName = isNameEncodedKey(row.key)
                    let decoded: string | null = null
                    if (isName) {
                      try { decoded = uint64ToName(row.value) } catch { decoded = null }
                    }
                    return (
                      <tr key={row.key} className="border-b border-[#f5f5f5] align-top">
                        <td className="py-2 pr-2 text-[#999]">{idx + 1}</td>
                        <td className="py-2 pr-2 font-mono">{row.key}</td>
                        <td className="py-2 pr-2">
                          {isEditing ? (
                            <Input
                              value={editingInput}
                              onChange={(e) => setEditingInput(e.target.value)}
                              placeholder={isName ? 'eos.account' : '0'}
                              autoFocus
                            />
                          ) : isName ? (
                            <div className="leading-tight">
                              <div>{decoded ?? '(invalid name)'}</div>
                              <div className="text-[12px] text-[#999] break-all">{row.value}</div>
                            </div>
                          ) : (
                            <div>{row.value}</div>
                          )}
                        </td>
                        <td className="py-2">
                          {isEditing ? (
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => submitEdit(row)} disabled={txLoading}>
                                {txLoading ? 'Saving…' : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={cancelEdit} disabled={txLoading}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => startEdit(row)}>
                              Edit
                            </Button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
